import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FieldLabel } from '@/components/FormField';

type NumberMode = 'auto' | 'custom';

/**
 * The page-header invoice number: shows the effective number (the override when set, otherwise the
 * server-suggested next number) and opens a popover to change it. An overridden number carries an
 * amber badge; automatic numbering is the quiet default and gets none. The popover names both states
 * as a two-button choice and commits on "Save" — picking "Automatic" and saving is what clears an
 * override; dismissing (Escape, outside click, "Cancel") keeps the number the draft already carries.
 */
export function InvoiceNumberHeading({
  suggestedNumber,
  value,
  onChange,
  disabled,
}: {
  suggestedNumber: string | null;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const overridden = value.trim() !== '';
  const effective = overridden ? value.trim() : suggestedNumber;

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<NumberMode>('auto');
  const [candidate, setCandidate] = useState('');

  const onOpenChange = (next: boolean) => {
    if (next) {
      setMode(overridden ? 'custom' : 'auto');
      setCandidate(overridden ? value.trim() : (suggestedNumber ?? ''));
    }
    setOpen(next);
  };

  const canSave = mode === 'auto' || candidate.trim() !== '';

  const save = () => {
    if (!canSave) return;
    onChange(mode === 'custom' ? candidate.trim() : '');
    setOpen(false);
  };

  return (
    <h1 className="flex min-w-0 items-center gap-1.5 text-2xl font-semibold tracking-tight">
      <span className="shrink-0 font-medium text-muted-foreground">
        {t('invoices.compose.title')}
        {effective ? ':' : ''}
      </span>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="min-w-0 text-2xl font-semibold tracking-tight"
            disabled={disabled}
            aria-label={t('invoices.compose.panel.number_edit')}
          >
            {effective ? <span className="min-w-0 truncate">{effective}</span> : null}
            {overridden ? (
              <Badge
                variant="outline"
                className="border-amber-500/40 text-amber-600 dark:text-amber-400"
              >
                {t('invoices.compose.panel.number_badge_custom')}
              </Badge>
            ) : null}
            <Pencil data-icon="inline-end" className="text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          <PopoverTitle>{t('invoices.compose.panel.number_title')}</PopoverTitle>
          <div
            className="flex gap-1"
            role="group"
            aria-label={t('invoices.compose.panel.number_title')}
          >
            <Button
              type="button"
              variant={mode === 'auto' ? 'default' : 'outline'}
              className="flex-1"
              aria-pressed={mode === 'auto'}
              onClick={() => setMode('auto')}
            >
              {t('invoices.compose.panel.number_mode_auto')}
            </Button>
            <Button
              type="button"
              variant={mode === 'custom' ? 'default' : 'outline'}
              className="flex-1"
              aria-pressed={mode === 'custom'}
              onClick={() => setMode('custom')}
            >
              {t('invoices.compose.panel.number_mode_custom')}
            </Button>
          </div>
          {mode === 'auto' ? (
            <div className="space-y-0.5">
              <p className="font-medium">
                {suggestedNumber ?? t('invoices.compose.panel.number_auto_unknown')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('invoices.compose.panel.number_auto_hint')}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <FieldLabel htmlFor="number-override">
                {t('invoices.compose.fields.number_override')}
              </FieldLabel>
              <Input
                id="number-override"
                autoComplete="off"
                autoFocus
                placeholder={suggestedNumber ?? undefined}
                value={candidate}
                onChange={(e) => setCandidate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    save();
                  }
                }}
              />
              <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                {t('invoices.compose.panel.override_warning')}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-1.5">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" disabled={!canSave} onClick={save}>
              {t('invoices.compose.panel.number_save')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </h1>
  );
}
